import { useEffect, useState } from 'react';

import { Icon } from './icon';
import { BlockedReason } from '../../../../backend/src/types/types';
import { useUserContext } from '@/Contexts/User';

const STORAGE_KEY = 'wexler:blockedPopupDismissed';

const TITLE = "You've hit your plan's credit limit.";

const describe = (reason: BlockedReason | null, blockedUntil: Date | null): string => {
  const resetClause =
    blockedUntil !== null
      ? `Your limit resets on ${new Date(blockedUntil).toLocaleDateString('en-GB')}.`
      : `Contact your administrator or support for help if you need a higher limit.`;

  const kim = ' You can still use Kim.';

  switch (reason) {
    case BlockedReason.FairUsageStreak:
      return `You've exceeded your credit limit for 3 consecutive months. ${resetClause} ${kim}`;
    case BlockedReason.ShadowLimitExceeded:
      return `You've exceeded the hard usage limit for this period. ${resetClause} ${kim}`;
    case BlockedReason.OrgLimitExceeded:
      return `Your organisation has exceeded its credit limit for this billing period. ${resetClause} ${kim}`;
    default:
      return `${resetClause} ${kim}`;
  }
};

/**
 * Sessionful popup shown when the signed-in user is blocked. `sessionStorage` scopes dismissal
 * to the tab lifetime — opening the webapp in a new tab/window re-shows it. Including the user
 * id and block state in the fingerprint means re-block after unblock (or switching user) also
 * re-shows, while identical state in the same tab stays dismissed.
 */
export function BlockedAccountDialog() {
  const { user } = useUserContext();

  const fingerprint =
    user?.isBlocked === true
      ? `${user.id}|${user.blockedReason ?? ''}|${user.blockedUntil !== null ? new Date(user.blockedUntil).toISOString() : ''}`
      : null;

  const [dismissed, setDismissed] = useState<string | null>(() =>
    typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) : null,
  );

  // Clear dismissal when the user becomes unblocked so a future re-block re-shows.
  useEffect(() => {
    if (fingerprint === null && dismissed !== null) {
      sessionStorage.removeItem(STORAGE_KEY);
      setDismissed(null);
    }
  }, [fingerprint, dismissed]);

  if (fingerprint === null || dismissed === fingerprint) return null;

  const description = describe(user!.blockedReason, user!.blockedUntil !== null ? new Date(user!.blockedUntil) : null);

  const handleDismiss = () => {
    sessionStorage.setItem(STORAGE_KEY, fingerprint);
    setDismissed(fingerprint);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 fixed bottom-8 left-1/2 z-50 w-full max-w-4xl -translate-x-1/2 px-4 duration-200">
      <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-lg">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{TITLE}</h3>
          <p className="mt-0.5 text-xs text-gray-600">{description}</p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="rounded-sm p-1 text-gray-400 opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
        >
          <Icon className="lucide--x h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
